/**
 * ShapeToolbar: Tool selection UI for markup drawing with style controls
 * Feature: 049-critique-photo-markup
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/atoms/Button';
import { Select } from '@/components/atoms/Select';
import { Slider } from '@/components/atoms/Slider';
import type { ShapeType, ShapeStyle, DashStyle } from '@/types/markup.types';
import { DASH_STYLE_MAP } from '@/types/markup.types';
import { getAllTools } from '@/utils/markupToolRegistry';
import { PointerIcon } from './ToolIcons';
import logger from '@/utils/logger';

interface ShapeToolbarProps {
  selectedTool: ShapeType | null;
  onToolSelect: (tool: ShapeType | null) => void;
  currentStyle: ShapeStyle;
  onStyleChange: (style: ShapeStyle) => void;
  disabled?: boolean;
}

/**
 * Convert RGBA hex (#RRGGBBAA) to separate color and opacity values
 */
function parseRGBAHex(rgba: string): { color: string; opacity: number } {
  if (rgba.length === 9 && rgba.startsWith('#')) {
    const color = rgba.slice(0, 7); // #RRGGBB
    const alpha = rgba.slice(7, 9); // AA
    const opacity = parseInt(alpha, 16) / 255;
    return { color, opacity };
  }
  return { color: '#FF0000', opacity: 1 };
}

/**
 * Convert color and opacity to RGBA hex (#RRGGBBAA)
 */
function toRGBAHex(color: string, opacity: number): string {
  const alpha = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, '0')
    .toUpperCase();
  return `${color}${alpha}`;
}

/**
 * Get dash style key from SVG strokeDasharray value
 */
function getDashStyleFromValue(dasharray: string): DashStyle {
  const entry = Object.entries(DASH_STYLE_MAP).find(([, value]) => value === dasharray);
  return (entry?.[0] as DashStyle) || 'solid';
}

export function ShapeToolbar({
  selectedTool,
  onToolSelect,
  currentStyle,
  onStyleChange,
  disabled = false
}: ShapeToolbarProps) {
  const tools = getAllTools();
  const { color, opacity } = parseRGBAHex(currentStyle.strokeColor);
  const [localColor, setLocalColor] = useState(color);
  const [localOpacity, setLocalOpacity] = useState(opacity);

  // Sync local state when currentStyle changes externally
  useEffect(() => {
    const { color: newColor, opacity: newOpacity } = parseRGBAHex(currentStyle.strokeColor);
    setLocalColor(newColor);
    setLocalOpacity(newOpacity);
  }, [currentStyle.strokeColor]);

  const handleToolSelect = (tool: ShapeType | null) => {
    logger.info('ShapeToolbar', 'Tool selected:', tool ?? 'pointer');
    onToolSelect(tool);
  };

  /**
   * Handle line thickness change from dropdown
   */
  const handleThicknessChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const strokeWidth = parseInt(e.target.value, 10);
    logger.debug('ShapeToolbar', 'Thickness changed:', strokeWidth);
    onStyleChange({
      ...currentStyle,
      strokeWidth,
    });
  };

  /**
   * Handle color picker change
   */
  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setLocalColor(newColor);
    const strokeColor = toRGBAHex(newColor, localOpacity);
    logger.debug('ShapeToolbar', 'Color changed:', strokeColor);
    onStyleChange({
      ...currentStyle,
      strokeColor,
    });
  };

  /**
   * Handle opacity slider change
   */
  const handleOpacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newOpacity = parseFloat(e.target.value);
    setLocalOpacity(newOpacity);
    const strokeColor = toRGBAHex(localColor, newOpacity);
    logger.debug('ShapeToolbar', 'Opacity changed:', newOpacity);
    onStyleChange({
      ...currentStyle,
      strokeColor,
    });
  };

  /**
   * Handle dash style dropdown change
   */
  const handleDashStyleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const dashStyle = e.target.value as DashStyle;
    const strokeDasharray = DASH_STYLE_MAP[dashStyle];
    logger.debug('ShapeToolbar', 'Dash style changed:', dashStyle);
    onStyleChange({
      ...currentStyle,
      strokeDasharray,
    });
  };

  const currentDashStyle = getDashStyleFromValue(currentStyle.strokeDasharray);

  return (
    <div className="flex items-center gap-4 p-4 bg-surface-1 border-b border-border">
      {/* Drawing tools */}
      <div className="flex gap-2">
        {/* Pointer tool (always visible, default selection) */}
        <Button
          type="button"
          onClick={() => handleToolSelect(null)}
          variant={selectedTool === null ? 'primary' : 'outline'}
          aria-label="Pointer tool"
          title="Pointer tool (select and move shapes)"
        >
          <PointerIcon />
        </Button>

        {/* Drawing tools from registry */}
        {tools.map((tool) => {
          const Icon = tool.icon;
          return (
            <Button
              key={tool.type}
              type="button"
              onClick={() => handleToolSelect(tool.type)}
              variant={selectedTool === tool.type ? 'primary' : 'outline'}
              disabled={disabled}
              aria-label={`${tool.label} tool`}
              title={`${tool.label} tool`}
            >
              {Icon ? <Icon /> : tool.label}
            </Button>
          );
        })}
      </div>

      {/* Divider */}
      <div className="h-8 w-px bg-border" />

      {/* Style controls */}
      <div className="flex items-center gap-4">
        {/* Visual preview of selected line style */}
        <svg width="62" height="24" className="flex-shrink-0">
          <line
            x1="2"
            y1="12"
            x2="60"
            y2="12"
            stroke={currentStyle.strokeColor}
            strokeWidth={currentStyle.strokeWidth}
            strokeDasharray={currentStyle.strokeDasharray}
          />
        </svg>

        {/* Line thickness dropdown with previews */}
        <div className="flex items-center gap-2">
          <Select
            id="thickness-select"
            value={currentStyle.strokeWidth.toString()}
            onChange={handleThicknessChange}
            disabled={disabled}
            options={[3, 4, 5, 6, 7, 8, 9, 10].map((thickness) => ({
              value: thickness.toString(),
              label: `${thickness}px`,
            }))}
            className="w-20 h-8 py-0"
          />
        </div>

        {/* Color picker button */}
        <div className="flex items-center gap-2">
          <input
            id="color-picker"
            type="color"
            value={localColor}
            onChange={handleColorChange}
            disabled={disabled}
            className="w-8 h-8 rounded border border-border cursor-pointer"
            title="Choose color"
          />
        </div>

        {/* Line style dropdown with previews */}
        <div className="flex items-center gap-2">
          <Select
            id="dash-style-select"
            value={currentDashStyle}
            onChange={handleDashStyleChange}
            disabled={disabled}
            options={[
              { value: 'solid', label: 'Solid' },
              { value: 'dashed', label: 'Dashed' },
              { value: 'dotted', label: 'Dotted' },
              { value: 'hand-drawn', label: 'Hand-Drawn' },
            ]}
            className="w-32 h-8 py-0"
          />
        </div>

        {/* Opacity slider */}
        <div className="flex items-center gap-2 min-w-[120px]">
          <label htmlFor="opacity-slider" className="text-xs text-muted whitespace-nowrap">
            Opacity
          </label>
          <Slider
            id="opacity-slider"
            min={0}
            max={1}
            step={0.01}
            value={localOpacity}
            onChange={handleOpacityChange}
            disabled={disabled}
            size="sm"
            className="w-16"
          />
          <span className="text-xs text-muted w-10 text-right">{Math.round(localOpacity * 100)}%</span>
        </div>

      </div>
    </div>
  );
}
